/* Visual size guide (snippets/size-guide.liquid).
   - Hover/focus a row or tap a poster on the wall -> previews that size (highlight + tag).
   - Click a row -> selects that size on the product page (variant drawer / radios / select)
     and closes the size drawer.
   - Follows the product page's current variant (variant:changed + form input). */
(() => {
  if (customElements.get('size-guide')) return;

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.rows = [...this.querySelectorAll('.sg-row')];
      this.posters = [...this.querySelectorAll('.sg-wall__poster')];
      this.tag = this.querySelector('[data-tag]');
      this.stage = this.querySelector('.sg-wall__stage');
      this.selected = this.dataset.current || '';
      this.drawer = this.closest('.size-drawer--main-container');

      this.rows.forEach((row) => {
        const size = row.dataset.size;
        row.addEventListener('mouseenter', () => this.preview(size));
        row.addEventListener('focus', () => this.preview(size));
        row.addEventListener('mouseleave', () => this.preview(null));
        row.addEventListener('blur', () => this.preview(null));
        row.addEventListener('click', () => this.choose(row));
      });
      this.posters.forEach((poster) => {
        poster.addEventListener('mouseenter', () => this.preview(poster.dataset.size));
        poster.addEventListener('mouseleave', () => this.preview(null));
        poster.addEventListener('focus', () => this.preview(poster.dataset.size));
        poster.addEventListener('blur', () => this.preview(null));
        // A poster on the wall selects its size exactly like its table row.
        poster.addEventListener('click', () => {
          const row = this.rowFor(poster.dataset.size);
          if (row && !row.disabled) this.choose(row);
          else this.preview(poster.dataset.size);
        });
      });

      document.addEventListener('variant:changed', (e) => {
        const v = e.detail && e.detail.variant;
        if (v) this.setSelected(this.sizeOfVariant(v));
      });

      // Re-sync with the page every time the drawer opens.
      if (this.drawer) {
        new MutationObserver(() => {
          if (this.drawer.classList.contains('size-drawer--open')) this.syncFromPage();
        }).observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
      }

      const moreBtn = this.querySelector('[data-more-toggle]');
      if (moreBtn) {
        moreBtn.addEventListener('click', () => {
          const body = this.querySelector(`#${moreBtn.getAttribute('aria-controls')}`);
          const open = moreBtn.getAttribute('aria-expanded') !== 'true';
          moreBtn.setAttribute('aria-expanded', String(open));
          if (body) body.hidden = !open;
        });
      }

      // custom.js drops main's 'side-drawer-open' (z-index 999) on ANY click inside an open size
      // drawer, which lets the announcement bar paint over the drawer. Restore it while open.
      if (this.drawer) {
        this.drawer.addEventListener('click', (e) => {
          // Clicks that close the drawer must not restore it.
          if (e.target.closest('.drawer-close--btn, .page-overlay, .sg-row, .sg-wall__poster')) return;
          if (!e.target.closest('.size-drawer-panel')) return;
          setTimeout(() => {
            const main = document.querySelector('main#MainContent');
            if (main && this.drawer.classList.contains('size-drawer--open')) main.classList.add('side-drawer-open');
          }, 0);
        });
      }

      this.bindExtraTriggers();
      this.render(this.selected);
      window.addEventListener('resize', () => this.render(this.current || this.selected), { passive: true });
    }

    // custom.js only binds the FIRST element carrying the drawer-name class (the desktop chip,
    // hidden on mobile). Forward clicks from the other triggers (e.g. the footer link inside the
    // mobile size sheet) to it, and close the size sheet so the guide is not stacked under it.
    bindExtraTriggers() {
      if (!this.drawer) return;
      const name = [...this.drawer.classList].find((c) =>
        (window.sizeDrawers || []).some((d) => d.drawer_name === c)
      );
      if (!name) return;
      const first = document.querySelector(`.${name}`);
      document.querySelectorAll(`.${name}`).forEach((el) => {
        if (el === first || el === this.drawer || el.dataset.sgBound) return;
        el.dataset.sgBound = '1';
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const sheet = el.closest('variant-drawer');
          if (sheet && typeof sheet.closeDrawerFunction === 'function') sheet.closeDrawerFunction();
          if (first) first.click();
        });
      });
    }

    get sizeIndex() {
      return parseInt(this.dataset.sizeIndex || '0', 10);
    }

    variants() {
      if (this._variants) return this._variants;
      let list = [];
      try {
        const el = document.querySelector('variant-drawer script[type="application/json"], variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
        if (el) list = JSON.parse(el.textContent);
      } catch (e) {
        list = [];
      }
      this._variants = list;
      return list;
    }

    sizeOfVariant(v) {
      return (v.options && v.options[this.sizeIndex]) || v[`option${this.sizeIndex + 1}`] || '';
    }

    rowFor(size) {
      return this.rows.find((r) => r.dataset.size === size);
    }

    productForm() {
      const pid = this.dataset.productId;
      return (
        document.querySelector(`form[data-product-id="${pid}"][action*="/cart/add"]`) ||
        document.querySelector('.product-main form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    syncFromPage() {
      const form = this.productForm();
      const input = form && form.querySelector('[name="id"]');
      const id = input && parseInt(input.value, 10);
      const v = id && this.variants().find((x) => x.id === id);
      if (v) this.setSelected(this.sizeOfVariant(v));
      else this.render(this.selected);
    }

    setSelected(size) {
      if (!size) return;
      this.selected = size;
      this.rows.forEach((r) => {
        const on = r.dataset.size === size;
        r.classList.toggle('is-active', on);
        r.setAttribute('aria-current', on ? 'true' : 'false');
      });
      this.render(size);
    }

    preview(size, sticky = false) {
      this.rows.forEach((r) => r.classList.toggle('is-preview', !!size && r.dataset.size === size && sticky));
      this.render(size || this.selected);
    }

    render(size) {
      this.current = size;
      let active = null;
      this.posters.forEach((p) => {
        const on = p.dataset.size === size;
        p.classList.toggle('is-active', on);
        if (on) active = p;
      });
      if (!this.tag) return;
      if (!active) {
        this.tag.textContent = '';
        return;
      }
      const row = this.rowFor(size);
      const label = row ? row.querySelector('.sg-row__size strong') : null;
      const cm = row ? row.querySelector('.sg-row__size small') : null;
      // data-tag is set (from snippets/size-label.liquid) only in inch countries.
      this.tag.textContent = row && row.dataset.tag ? row.dataset.tag : cm ?`${label.textContent.trim()} · ${cm.textContent.trim()}` : label ? label.textContent.trim() : size;
      const left = parseFloat(active.style.left) + parseFloat(active.style.width) / 2;
      const bottom = parseFloat(active.style.bottom) + parseFloat(active.style.height);
      this.tag.style.left = `${left}%`;
      this.tag.style.bottom = `${bottom}%`;
      // Keep the tag inside the stage horizontally.
      requestAnimationFrame(() => {
        if (!this.stage) return;
        const sw = this.stage.clientWidth;
        const tw = this.tag.offsetWidth;
        const px = (left / 100) * sw;
        const clamped = Math.min(Math.max(px, tw / 2), sw - tw / 2);
        this.tag.style.left = `${(clamped / sw) * 100}%`;
      });
    }

    targetVariantId(row) {
      const size = row.dataset.size;
      const list = this.variants();
      if (!list.length) return parseInt(row.dataset.variantId, 10);
      // Keep the other options (if any) of the current variant.
      const form = this.productForm();
      const input = form && form.querySelector('[name="id"]');
      const cur = input && list.find((x) => x.id === parseInt(input.value, 10));
      const match = list.find((v) => {
        const opts = v.options || [];
        if (opts[this.sizeIndex] !== size) return false;
        if (!cur) return true;
        return opts.every((o, i) => i === this.sizeIndex || o === cur.options[i]);
      });
      return match ? match.id : parseInt(row.dataset.variantId, 10);
    }

    choose(row) {
      if (row.disabled) return;
      const size = row.dataset.size;
      const id = this.targetVariantId(row);
      const pid = this.dataset.productId;

      try {
        this.applyToPage(id, size, pid);
      } catch (err) {
        console.warn('[size-guide] could not apply size', err);
      }

      this.setSelected(size);
      const close = this.drawer && this.drawer.querySelector('.drawer-close--btn');
      if (close) setTimeout(() => close.click(), 180);
    }

    applyToPage(id, size, pid) {
      const vd = document.querySelector(`variant-drawer[data-product-id="${pid}"]`) || document.querySelector('variant-drawer');
      if (vd && typeof vd.onVariantChange === 'function') {
        vd.onVariantChange(id);
      } else {
        const esc = window.CSS && CSS.escape ? CSS.escape(size) : size;
        const radio = document.querySelector(`variant-radios input[type="radio"][value="${esc}"]`);
        const select = [...document.querySelectorAll('variant-selects select')].find((s) => [...s.options].some((o) => o.value === size));
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (select) {
          select.value = size;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  customElements.define('size-guide', SizeGuide);
})();
